/**
 * DFP Signal Anomaly Alerts Service
 * Fetches signal anomaly alerts from the DFP detection engine
 */

export interface DFPAlert {
  id: number;
  anomaly_type: string;
  severity: string;  // 'info', 'warning', 'critical'
  signal_type: string;  // 'cellular', 'wifi', 'gnss', 'bluetooth'
  device_id: string;
  latitude?: number;
  longitude?: number;
  detected_at: string;
  confidence_score?: number;
  status: string;  // 'open', 'acknowledged', 'resolved', 'false_positive'
  evidence?: Record<string, any>;
  observation_count?: number;
  first_seen?: string;
  last_seen?: string;
  resolved_at?: string;
  feedback_verdict?: 'true_positive' | 'false_positive' | 'unclear' | null;
  feedback_submitted_at?: string | null;
  feedback_submitted_by?: string | null;
}

export interface DFPAlertResponse {
  alerts: DFPAlert[];
  count: number;
  total: number;
}

export interface DFPCopAlertSettings {
  cop_poll_status: 'open' | 'unacknowledged';
  alert_auto_dismiss_hours: number;
}

export interface RFEntityDossier {
  entity_type: string;
  stable_identifier: string;
  normalized_identifier: string;
  label?: string | null;
  security?: string | null;
  oui_manufacturer?: string | null;
  first_seen?: string | null;
  last_seen?: string | null;
  obs_count?: number | null;
  day_count?: number | null;
  distinct_observer_count?: number | null;
  centroid_lat?: number | null;
  centroid_lon?: number | null;
  radius_m?: number | null;
  is_open?: boolean | null;
  is_la_bit?: boolean | null;
  is_hidden?: boolean | null;
  is_rotating?: boolean | null;
  is_tracker_oui?: boolean | null;
  address_kind?: string | null;
  foreign_cc?: string | null;
  ssid_variant_count?: number | null;
  security_variant_count?: number | null;
  posture?: Record<string, any> | null;
  verdict_state?: string | null;
  verdict_reason?: string | null;
  verdict_evidence?: Record<string, any> | null;
  verdict_updated_at?: string | null;
  prev_verdict_state?: string | null;
  ai_verdict?: string | null;
  ai_rationale?: string | null;
  ai_verdict_at?: string | null;
  operator_verdict?: 'benign' | 'threat' | null;
  operator_verdict_by?: string | null;
  operator_verdict_at?: string | null;
  observations?: {
    observers: Array<{
      device_id: string;
      sightings: number;
      first_seen: string;
      last_seen: string;
      min_rssi: number | null;
      max_rssi: number | null;
      avg_rssi: number | null;
    }>;
    recent: Array<{
      recorded_at: string;
      device_id: string;
      rssi: number | null;
      latitude: number | null;
      longitude: number | null;
      label: string | null;
      security: string | null;
      frequency_mhz: number | null;
      channel: number | null;
      standard: string | null;
    }>;
    estimated_position: {
      latitude: number;
      longitude: number;
      method: string;
      observer_count: number;
      best_rssi: number | null;
      accuracy_m?: number | null;
    } | null;
  } | null;
  position_track?: Array<{
    t: string;
    latitude: number;
    longitude: number;
    observers: number;
    single_observer: boolean;
  }>;
  timing_advance?: {
    ta: number;
    range_m: number;
    range_step_m: number;
    category: string | null;
    observer_lat: number | null;
    observer_lon: number | null;
    observed_at: string | null;
  } | null;
  tdoa_position?: {
    estimated_lat: number;
    estimated_lon: number;
    accuracy_m: number | null;
    method: string;
    phone_count: number;
    observed_at: string | null;
  } | null;
  registered_tower?: {
    latitude: number;
    longitude: number;
    range_m: number | null;
    radio: string | null;
    samples: number | null;
    updated_at: string | null;
  } | null;
}

export interface DFPHealthResponse {
  status: string;
  detectors: Array<{
    detector_name: string;
    status: string;
    observations_count: number;
    baseline_days: number;
    last_updated: string;
  }>;
  alert_stats: Record<string, number>;
  active_dedup_entries: number;
  verdict_pipeline?: {
    ledger?: {
      total: number;
      state_new: number;
      state_watch: number;
      state_established: number;
      state_confirmed: number;
      state_inconclusive: number;
      ai_adjudicated: number;
      last_ai_verdict_at: string | null;
      operator_benign: number;
      operator_threat: number;
      ledger_last_seen: string | null;
    };
    verdict_activity?: {
      evaluations_24h: number;
      would_alert_24h: number;
      confirmed_transitions_24h: number;
      last_evaluated_at: string | null;
    };
    reference_data?: {
      towers: number;
      opencellid_last_refresh?: string | null;
    };
  };
}

const ANOMALY_TYPE_LABELS: Record<string, string> = {
  imsi_catcher_suspect: 'Rogue Base Station',
  rogue_access_point: 'Rogue AP',
  personal_hotspot: 'Personal Hotspot',
  evil_twin: 'Evil Twin',
  cotraveler: 'Co-Traveler',
  drone_detected: 'Drone',
  unknown_device: 'Unknown Device',
  mac_anomaly: 'MAC Anomaly',
  gnss_spoofing: 'GNSS Spoofing',
  jamming_gnss: 'GNSS Jamming',
  ble_located: 'BLE Located',
  wifi_located: 'WiFi Located',
  cell_located: 'Cell Located',
  watchlist_hit: 'Watchlist Hit',
  ble_flood: 'BLE Flood',
  // Non-dropdown / legacy types that may still appear in feeds
  watchlist_proximity: 'Watchlist Proximity',
  connection_downgrade: 'Connection Downgrade',
  shadowed_device: 'Shadowed Device',
  jamming_wifi: 'WiFi Jamming',
  jamming_cellular: 'Cellular Jamming',
  abnormal_signal_strength: 'Signal Anomaly',
  dwell_anomaly: 'Dwell Anomaly',
};

const SIGNAL_TYPE_LABELS: Record<string, string> = {
  cellular: 'Cellular',
  wifi: 'WiFi',
  gnss: 'GNSS',
  bluetooth: 'Bluetooth',
  location: 'Location',
  drone_remoteid: 'Remote ID',
};

export function getAnomalyLabel(type: string): string {
  return ANOMALY_TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function getSignalTypeLabel(type: string): string {
  return SIGNAL_TYPE_LABELS[type] || type;
}

class DFPAlertsService {
  private baseUrl = process.env.REACT_APP_API_URL || '/api';
  private pollingInterval: NodeJS.Timeout | null = null;

  private getHeaders() {
    return {
      'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
      'Content-Type': 'application/json',
    };
  }

  async getAlerts(params?: {
    anomaly_type?: string;
    category?: 'actionable' | 'insights' | 'all';
    severity?: string;
    signal_type?: string;
    status?: string;
    device_id?: string;
    hours?: number;
    limit?: number;
    include_total?: boolean;
    include_feedback?: boolean;
  }): Promise<DFPAlertResponse> {
    const query = new URLSearchParams();
    if (params?.anomaly_type) query.append('anomaly_type', params.anomaly_type);
    if (params?.category) query.append('category', params.category);
    if (params?.severity) query.append('severity', params.severity);
    if (params?.signal_type) query.append('signal_type', params.signal_type);
    if (params?.status) query.append('status', params.status ?? 'open');
    if (params?.device_id) query.append('device_id', params.device_id);
    query.append('hours', String(params?.hours ?? 24));
    query.append('limit', String(params?.limit ?? 200));
    if (params?.include_total === false) query.append('include_total', 'false');
    if (params?.include_feedback === false) query.append('include_feedback', 'false');

    const resp = await fetch(`${this.baseUrl}/dfp/alerts?${query}`, { headers: this.getHeaders() });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return resp.json();
  }

  /**
   * Viewer-readable COP poll settings (admin-configured via DFP Detection Settings).
   * Defaults to open / 8h when the endpoint is unavailable.
   */
  async getCopAlertSettings(): Promise<DFPCopAlertSettings> {
    try {
      const resp = await fetch(`${this.baseUrl}/dfp/cop-alert-settings`, {
        headers: this.getHeaders(),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const poll = data?.cop_poll_status === 'unacknowledged' ? 'unacknowledged' : 'open';
      const hours = Number(data?.alert_auto_dismiss_hours);
      return {
        cop_poll_status: poll,
        alert_auto_dismiss_hours: Number.isFinite(hours)
          ? Math.max(0, Math.min(168, hours))
          : 8,
      };
    } catch (err) {
      console.warn('[DFP] Could not load COP alert settings; defaulting to open:', err);
      return { cop_poll_status: 'open', alert_auto_dismiss_hours: 8 };
    }
  }

  async getAlertDetail(id: number): Promise<DFPAlert & Record<string, any>> {
    const resp = await fetch(`${this.baseUrl}/dfp/alerts/${id}`, { headers: this.getHeaders() });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return resp.json();
  }

  async acknowledgeAlert(id: number, action: string = 'acknowledged', notes?: string): Promise<void> {
    const query = new URLSearchParams({ action });
    if (notes) query.append('notes', notes);
    const resp = await fetch(`${this.baseUrl}/dfp/alerts/${id}/acknowledge?${query}`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  }

  async getHealth(): Promise<DFPHealthResponse> {
    const resp = await fetch(`${this.baseUrl}/dfp/health`, { headers: this.getHeaders() });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return resp.json();
  }

  async getSummaryByType(hours: number = 24): Promise<any> {
    const resp = await fetch(`${this.baseUrl}/dfp/alerts/summary/by-type?hours=${hours}`, { headers: this.getHeaders() });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return resp.json();
  }

  async getExplanation(alertId: number): Promise<{
    why: string;
    what_it_means: string;
    score_breakdown: [string, number, string][];
    false_positive_hints: string[];
  }> {
    const resp = await fetch(`${this.baseUrl}/dfp/alerts/${alertId}/explanation`, { headers: this.getHeaders() });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return resp.json();
  }

  async submitFeedback(alertId: number, verdict: 'true_positive' | 'false_positive' | 'unclear', note?: string): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/dfp/alerts/${alertId}/feedback`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ verdict, note }),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  }

  /** Full ledger dossier for one RF entity (verdict pipeline). */
  async getEntityDossier(entityType: string, identifier: string): Promise<RFEntityDossier> {
    const resp = await fetch(
      `${this.baseUrl}/dfp/entities/${entityType}/${encodeURIComponent(identifier)}`,
      { headers: this.getHeaders() },
    );
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return resp.json();
  }

  /** One-tap analyst verdict written straight to the entity ledger. */
  async setEntityVerdict(
    entityType: string,
    identifier: string,
    verdict: 'benign' | 'threat' | null,
  ): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/dfp/entities/verdict`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ entity_type: entityType, identifier, verdict }),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  }

  /**
   * Start polling DFP alerts.
   *
   * COP defaults to status='open' (admin-configurable via
   * getCopAlertSettings / DFP Detection Settings). Pass
   * status='unacknowledged' for open + auto-resolved, or status='all'
   * for the full history window.
   *
   * @param callback   Invoked with the alerts each poll cycle.
   * @param intervalMs Poll cadence (ms). Defaults to 15s.
   * @param hours      Lookback window in hours. Defaults to 24h. Pass a wider
   *                   value (e.g. 168 for "Last Week", 720 for "All Time") to
   *                   honor the COP top-bar date dropdown.
   * @param status     Status filter ('all' | 'open' | 'unacknowledged' |
   *                   'acknowledged' | 'resolved' | 'false_positive').
   *                   Defaults to 'open'.
   */
  startPolling(
    callback: (alerts: DFPAlert[]) => void,
    intervalMs: number = 15000,
    hours: number = 24,
    status: string = 'open',
    category?: 'actionable' | 'insights' | 'all',
  ) {
    this.stopPolling();
    // Background COP poll: short window + small page, no COUNT(*) / feedback
    // join. The Alerts tab still loads the full date-bar window on demand.
    const pollHours = Math.min(Math.max(hours, 1), 8);
    const limit = 200;
    const poll = async () => {
      try {
        const resp = await this.getAlerts({
          status,
          hours: pollHours,
          limit,
          category,
          include_total: false,
          include_feedback: false,
        });
        callback(resp.alerts);
      } catch (err) {
        console.error('[DFP] Poll error:', err);
      }
    };
    poll();
    this.pollingInterval = setInterval(poll, intervalMs);
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }
}

export const dfpAlertsService = new DFPAlertsService();
